import { jsxs, jsx } from 'react/jsx-runtime';
import 'date-fns-tz';
import { cn } from '../../utils/formatters.js';
import { Label } from './label.js';
import './alert.js';
import '@radix-ui/react-avatar';
import './badge.js';
import 'lucide-react';
import * as React from 'react';
import 'react-day-picker';
import './button.js';
import './chart.js';
import '@radix-ui/react-checkbox';
import '@radix-ui/react-collapsible';
import 'cmdk';
import '@radix-ui/react-dialog';
import { DatePicker } from './date-picker.js';
import { TimePicker } from './time-picker.js';
import 'vaul';
import '@radix-ui/react-dropdown-menu';
import '@radix-ui/react-popover';
import '@radix-ui/react-progress';
import './radio-group.js';
import '@radix-ui/react-select';
import './time-input.js';
import '@radix-ui/react-separator';
import './sidebar.js';
import 'react-toastify';
import '@radix-ui/react-switch';
import '@radix-ui/react-tabs';
import '@radix-ui/react-tooltip';
import '@clerk/clerk-react';
import '../../lib/toast.js';
import 'react-router-dom';
import '../../contexts/AppContext.js';
import '../../contexts/OrganizationContext.js';
import 'axios';
import '@tanstack/react-query';
import '../page-layout/FormPageLayout.js';
import '../form/RichTextEditor.js';
import '../crud/CrudDetailView.js';
import '../crud/CrudFormView.js';
import '../../contexts/SidebarContext.js';
import '../../contexts/ThemeContext.js';
import '@dnd-kit/core';
import '@dnd-kit/sortable';
import '@dnd-kit/utilities';

/**
 * Combined date and time picker component
 * Handles UTC datetime conversion to ensure consistent datetime storage
 * @param date - Selected date/time value (Date object or ISO string)
 * @param onDateTimeChange - Callback when date/time changes (returns Date in UTC)
 * @param disabled - Whether the picker is disabled
 * @param className - Additional CSS classes for container
 * @param withSeconds - Whether to include seconds in time picker
 * @param dateLabel - Label for date picker
 * @param timeLabel - Label for time picker
 * @param useUtc - Whether to use UTC dates (defaults to true)
 */
function DateTimePicker({ date, onDateTimeChange, disabled = false, className, withSeconds = false, dateLabel = 'Date', timeLabel = 'Time', useUtc = true, }) {
    // Parse the date value to a Date object
    const parsedDate = React.useMemo(() => {
        if (!date)
            return undefined;
        if (date instanceof Date)
            return date;
        return new Date(date);
    }, [date]);
    const [selectedDate, setSelectedDate] = React.useState(parsedDate);
    const [timeValue, setTimeValue] = React.useState(() => {
        if (!parsedDate)
            return '';
        const hours = useUtc
            ? parsedDate.getUTCHours().toString().padStart(2, '0')
            : parsedDate.getHours().toString().padStart(2, '0');
        const minutes = useUtc
            ? parsedDate.getUTCMinutes().toString().padStart(2, '0')
            : parsedDate.getMinutes().toString().padStart(2, '0');
        const seconds = useUtc
            ? parsedDate.getUTCSeconds().toString().padStart(2, '0')
            : parsedDate.getSeconds().toString().padStart(2, '0');
        return withSeconds ? `${hours}:${minutes}:${seconds}` : `${hours}:${minutes}`;
    });
    React.useEffect(() => {
        setSelectedDate(parsedDate);
        if (parsedDate) {
            const hours = useUtc
                ? parsedDate.getUTCHours().toString().padStart(2, '0')
                : parsedDate.getHours().toString().padStart(2, '0');
            const minutes = useUtc
                ? parsedDate.getUTCMinutes().toString().padStart(2, '0')
                : parsedDate.getMinutes().toString().padStart(2, '0');
            const seconds = useUtc
                ? parsedDate.getUTCSeconds().toString().padStart(2, '0')
                : parsedDate.getSeconds().toString().padStart(2, '0');
            setTimeValue(withSeconds ? `${hours}:${minutes}:${seconds}` : `${hours}:${minutes}`);
        }
    }, [parsedDate, withSeconds, useUtc]);
    const handleDateChange = (newDate) => {
        setSelectedDate(newDate);
        if (!newDate) {
            onDateTimeChange?.(undefined);
            return;
        }
        // Preserve time if it exists
        if (timeValue) {
            const [hours, minutes, seconds = '0'] = timeValue.split(':');
            let combined;
            if (useUtc) {
                // Create UTC datetime
                combined = new Date(Date.UTC(newDate.getFullYear(), newDate.getMonth(), newDate.getDate(), parseInt(hours, 10), parseInt(minutes, 10), parseInt(seconds, 10)));
            }
            else {
                combined = new Date(newDate);
                combined.setHours(parseInt(hours, 10), parseInt(minutes, 10), parseInt(seconds, 10));
            }
            onDateTimeChange?.(combined);
        }
        else {
            onDateTimeChange?.(newDate);
        }
    };
    const handleTimeChange = (newTime) => {
        setTimeValue(newTime);
        if (!selectedDate)
            return;
        const [hours, minutes, seconds = '0'] = newTime.split(':');
        let combined;
        if (useUtc) {
            // Create UTC datetime
            combined = new Date(Date.UTC(selectedDate.getUTCFullYear(), selectedDate.getUTCMonth(), selectedDate.getUTCDate(), parseInt(hours, 10), parseInt(minutes, 10), parseInt(seconds, 10)));
        }
        else {
            combined = new Date(selectedDate);
            combined.setHours(parseInt(hours, 10), parseInt(minutes, 10), parseInt(seconds, 10));
        }
        onDateTimeChange?.(combined);
    };
    return (jsxs("div", { className: cn('flex gap-4', className), children: [jsxs("div", { className: "flex flex-1 flex-col gap-3", children: [dateLabel ? jsx(Label, { className: "px-1", children: dateLabel }) : null, jsx(DatePicker, { date: selectedDate, onDateChange: handleDateChange, disabled: disabled, useUtc: false })] }), jsxs("div", { className: "flex flex-1 flex-col gap-3", children: [timeLabel ? jsx(Label, { className: "px-1", children: timeLabel }) : null, jsx(TimePicker, { time: timeValue, onTimeChange: handleTimeChange, disabled: disabled, withSeconds: withSeconds })] })] }));
}

export { DateTimePicker };
//# sourceMappingURL=datetime-picker.js.map
